(ns nl.mediquest.questionnaire-frontend.component.questionnaire-menu
  "Provides a page to click-through to one of multiple questionnaire-ids.
   Optionally, with a questionnaire-deployment-id, only shows the questionnaires
   for which a deployment with that questionnaire-deployment-id is actually available.
   Auto redirects when no menu can be made, i.e. 0 or 1 questionnaires are left."
  (:require
   [ajax.core :as ajax]
   [nl.mediquest.questionnaire-frontend.config :as config]
   [nl.mediquest.questionnaire-frontend.effects :as effects]
   [nl.mediquest.ui.core.notifications :as notifications]
   [re-frame.core :as re-frame]))

(def questionnaire-menus-config
  "A map of questionnaire-menu-ids to questionnaire-menu-config
   A questionnaire-menu-config maps all questionnaire-ids in the questionnaire-menu
   to button titles and availability."
  {"mijn-positieve-gezondheid"
   {::menu-options
    {"mijn-positieve-gezondheid"
     {::title "Positieve gezondheid: Volwassenen"
      ::available? true}
     "mijn-positieve-gezondheid-kind"
     {::title "Positieve gezondheid: Kind"
      ::available? true}
     "mijn-positieve-gezondheid-jongeren"
     {::title "Positieve gezondheid: Jongeren"
      ::available? true}
     "mijn-positieve-gezondheid-eenvoudig"
     {::title "Positieve gezondheid: Eenvoudig"
      ::available? true}}}})

(re-frame/reg-sub
 ::questionnaire-menu-id
 :questionnaire/menu-id)

(re-frame/reg-sub
 ::questionnaire-deployment-id
 :questionnaire/questionnaire-deployment-id)

(re-frame/reg-sub
 ::fetching?
 (fn [db]
   (seq (::questionnaire-ids-pending-status-fetch db))))

(re-frame/reg-sub
 ::questionnaire-menus
 (fn [db]
   (::questionnaire-menus db)))

(re-frame/reg-sub
 ::questionnaire-options
 :<- [::questionnaire-menus]
 :<- [::questionnaire-menu-id]
 (fn [[questionnaire-menus menu-id]]
   (get-in questionnaire-menus [menu-id ::menu-options])))

(defn- fetch-deploy-status-url [questionnaire-id questionnaire-deployment-id]
  (str config/BACKEND_HOST
       "/v1/"
       questionnaire-id
       "/"
       questionnaire-deployment-id
       "/deploy-status"))

(defn- fetch-deploy-status-http-xhrio [questionnaire-id questionnaire-deployment-id]
  [:http-xhrio
   {:method :get
    :uri (fetch-deploy-status-url questionnaire-id questionnaire-deployment-id)
    :format (ajax/json-request-format)
    :response-format (ajax/json-response-format {:keywords? true})
    :on-success [::success-fetch-deploy-status questionnaire-id]
    :on-failure [::failure-fetch-deploy-status questionnaire-id]}])

(re-frame/reg-event-fx
 ::fetch-options-deploy-status
 (fn [{{:questionnaire/keys [menu-id questionnaire-deployment-id] :as db} :db :as _cofx} _event]
   (when-let [menu-questionnaire-ids (keys (get-in questionnaire-menus-config [menu-id ::menu-options]))]
     (let [deploy-status-http-xhrio (mapv
                                     #(fetch-deploy-status-http-xhrio % questionnaire-deployment-id)
                                     menu-questionnaire-ids)]
       {:db (assoc db ::questionnaire-ids-pending-status-fetch (set menu-questionnaire-ids))
        :fx deploy-status-http-xhrio}))))

(re-frame/reg-event-fx
 ::init
 (fn [{{:questionnaire/keys [menu-id questionnaire-deployment-id] :as db} :db :as _cofx} _event]
   (if (get questionnaire-menus-config menu-id)
     (cond-> {:db (assoc db ::questionnaire-menus questionnaire-menus-config)}
       questionnaire-deployment-id (assoc :dispatch [::fetch-options-deploy-status]))
     {::effects/set-url {:url ""}
      :dispatch [::notifications/open
                 {:notification/description "De pagina die u zoekt kan niet worden gevonden."
                  :notification/level :level/error}]})))

(defn- clear-notifications-after-last [{::keys [questionnaire-ids-pending-status-fetch] :as db}]
  (if (seq questionnaire-ids-pending-status-fetch)
    (notifications/clear-notifications db)
    db))

(defn- available-questionnaire-ids [questionnaire-menus menu-id]
  (let [{::keys [menu-options]} (get questionnaire-menus menu-id)]
    (map key (filter (comp ::available? val) menu-options))))

(defn- auto-redirect-when-no-option
  "After all deploy statuses have been checked and the user has no option, auto redirect.
   When a single option is left, navigate to it.
   When no options are left, navigate to questionnaire-id for proper error message."
  [{{:questionnaire/keys [menu-id questionnaire-deployment-id] ::keys [questionnaire-menus] :as db} :db :as cofx} questionnaire-id]
  (if (empty? (::questionnaire-ids-pending-status-fetch db))
    (let [available-questionnaire-ids' (available-questionnaire-ids questionnaire-menus menu-id)]
      (case (count available-questionnaire-ids')
        0 (update cofx :fx (fnil conj []) [::effects/set-url {:url (str questionnaire-id "/" questionnaire-deployment-id)}])
        1 (update cofx :fx (fnil conj []) [::effects/set-url {:url (str (first available-questionnaire-ids') "/" questionnaire-deployment-id)}])
        cofx))
    cofx))

(defn- available-deployment?
  [{:questionnaire/keys [expired? max-per-questionnaire-deployment-id number-filled-out] :as deploy-status}]
  (and
   deploy-status
   (not expired?)
   (or
    (nil? max-per-questionnaire-deployment-id)
    (pos? (- max-per-questionnaire-deployment-id number-filled-out)))))

(re-frame/reg-event-fx
 ::success-fetch-deploy-status
 (fn [{{menu-id :questionnaire/menu-id} :db :as cofx} [_ questionnaire-id {:keys [deploy-status]}]]
   (-> cofx
       (assoc-in [:db ::questionnaire-menus menu-id ::menu-options questionnaire-id ::available?]
                 (available-deployment? deploy-status))
       (update-in [:db ::questionnaire-ids-pending-status-fetch] disj questionnaire-id)
       (update :db clear-notifications-after-last)
       (auto-redirect-when-no-option questionnaire-id))))

(re-frame/reg-event-fx
 ::failure-fetch-deploy-status
 (fn [{{menu-id :questionnaire/menu-id} :db :as cofx} [_ questionnaire-id _result]]
   (-> cofx
       (update-in [:db ::questionnaire-ids-pending-status-fetch] disj questionnaire-id)
       (assoc-in [:db ::questionnaire-menus menu-id ::menu-options questionnaire-id ::available?] false)
       (auto-redirect-when-no-option questionnaire-id))))

(defn option-button [questionnaire-id option-title]
  [:a.text-center.inline-block.p-4.outline.outline-1.outline-primary.no-underline.font-bold.text-primary.rounded-md.bg-white.hover:bg-purple-100
   {:href
    (if-let [questionnaire-deployment-id @(re-frame/subscribe [::questionnaire-deployment-id])]
      (str "/" questionnaire-id "/" questionnaire-deployment-id)
      (str "/" questionnaire-id))}
   option-title])

(defn view []
  [:div.mx-2.flex.flex-wrap.gap-2.justify-center
   (if @(re-frame/subscribe [::fetching?])
     [:p "Loading..."]
     (when-let [questionnaire-options (seq @(re-frame/subscribe [::questionnaire-options]))]
       (doall
        (for [[questionnaire-id {::keys [available? title]}] questionnaire-options
              :when available?]
          ^{:key questionnaire-id}
          [option-button questionnaire-id title]))))])
